import { ApiError } from "@/pages/auth/api";
import { API_BASE_URL } from "@/shared/config/apiBaseUrl";

import type { OpeningSeedSelection } from "./openingSeed/openingSeedTypes";
import type { PlayEngineVersion } from "./engineVersion";

export type CreatePlayGamePayload = {
  modelId: string;
  elo: number;
  userColor: "white" | "black";
  hardness: number;
  engineVersion: PlayEngineVersion;
  minutes: number;
  incrementSeconds: number;
  openingSeed?: OpeningSeedSelection | null;
};

export type PlayGameSnapshot = {
  game_id: string;
  version: number;
  state: string;
  moves: string[];
  initial_fen: string | null;
  opening_seed_ply?: number;
  opening_seed_source?: string | null;
  opening_seed_label?: string | null;
  user_username?: string | null;
  model_id: string;
  elo: number;
  hardness: number;
  temperature: number;
  engine_version: PlayEngineVersion;
  user_color: string;
  model_color: string;
  time_control: {
    minutes: number;
    increment_seconds: number;
  };
  user_clock_ms: number;
  model_clock_ms: number;
  active_side: string;
  last_move_started_at: string;
  pending_model_task_id?: string | null;
  pending_model_move?: string | null;
  model_send_at?: string | null;
  result: {
    winner: string | null;
    reason: string | null;
  } | null;
  created_at?: string | null;
  updated_at?: string | null;
};

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail = `request failed with status ${response.status}`;
    try {
      const payload = (await response.json()) as { detail?: string };
      if (payload.detail) {
        detail = payload.detail;
      }
    } catch {
      // Keep fallback message when response body is not JSON.
    }
    throw new ApiError(detail, response.status);
  }

  return (await response.json()) as T;
}

export async function createPlayGame(
  sessionToken: string,
  payload: CreatePlayGamePayload,
): Promise<PlayGameSnapshot> {
  const response = await fetch(`${API_BASE_URL}/api/play-model/games`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify({
      model_id: payload.modelId,
      elo: payload.elo,
      hardness: payload.hardness,
      engine_version: payload.engineVersion,
      user_color: payload.userColor,
      time_control: {
        minutes: payload.minutes,
        increment_seconds: payload.incrementSeconds,
      },
      initial_fen: null,
      opening_seed: payload.openingSeed
        ? {
            source: payload.openingSeed.source,
            moves: payload.openingSeed.moves,
            selected_ply: payload.openingSeed.moves.length,
            label: payload.openingSeed.label ?? null,
          }
        : null,
    }),
  });

  return parseJson<PlayGameSnapshot>(response);
}

export async function getPlayGame(
  sessionToken: string,
  gameId: string,
): Promise<PlayGameSnapshot> {
  const response = await fetch(`${API_BASE_URL}/api/play-model/games/${gameId}`, {
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
  });
  return parseJson<PlayGameSnapshot>(response);
}

export async function getPublicPlayGame(gameId: string): Promise<PlayGameSnapshot> {
  const response = await fetch(`${API_BASE_URL}/api/play-model/games/${gameId}/public`);
  return parseJson<PlayGameSnapshot>(response);
}

export async function getActivePlayGame(
  sessionToken: string,
): Promise<PlayGameSnapshot | null> {
  const response = await fetch(`${API_BASE_URL}/api/play-model/games/active`, {
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
  });

  if (response.status === 404) {
    return null;
  }

  return parseJson<PlayGameSnapshot>(response);
}

export async function submitPlayMove(
  sessionToken: string,
  gameId: string,
  move: string,
  expectedVersion: number,
): Promise<PlayGameSnapshot> {
  const response = await fetch(`${API_BASE_URL}/api/play-model/games/${gameId}/moves`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify({
      move,
      expected_version: expectedVersion,
    }),
  });
  return parseJson<PlayGameSnapshot>(response);
}

export async function syncPlayGame(
  sessionToken: string,
  gameId: string,
  expectedVersion?: number,
): Promise<PlayGameSnapshot> {
  const response = await fetch(`${API_BASE_URL}/api/play-model/games/${gameId}/sync`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify({
      expected_version: expectedVersion ?? null,
    }),
  });
  return parseJson<PlayGameSnapshot>(response);
}

export async function syncPublicPlayGame(gameId: string): Promise<PlayGameSnapshot> {
  const response = await fetch(`${API_BASE_URL}/api/play-model/games/${gameId}/public/sync`, {
    method: "POST",
  });
  return parseJson<PlayGameSnapshot>(response);
}

export async function resignPlayGame(
  sessionToken: string,
  gameId: string,
  expectedVersion?: number,
): Promise<PlayGameSnapshot> {
  const response = await fetch(`${API_BASE_URL}/api/play-model/games/${gameId}/resign`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify({
      expected_version: expectedVersion ?? null,
    }),
  });
  return parseJson<PlayGameSnapshot>(response);
}

export async function getPlayGamePgn(
  sessionToken: string,
  gameId: string,
): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/play-model/games/${gameId}/pgn`, {
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
  });

  const payload = await parseJson<{ pgn: string }>(response);
  return payload.pgn;
}
